import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { D1_SCHOOLS } from '../data/d1-schools';
import { ChevronDown, CheckCircle2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['Athletic Department Admin', 'Compliance / Risk', 'NIL Director', 'Coach', 'Agent / Representative', 'Athlete', 'Parent / Family', 'Other'];
const SPORTS = ['Football', "Men's Basketball", "Women's Basketball", 'Baseball', 'Ice Hockey', 'Soccer', 'Volleyball', 'Track & Field', 'Swimming', 'Other'];

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);

const Nav = () => (
  <nav className="border-b border-[#E5E7EB]">
    <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/insurance" className="flex items-center gap-2">
        <img src="/nil-resources/logos/logo-transparent.png" alt="Players Health" className="h-8" />
      </Link>
      <Link to="/insurance" className="text-[#6B7280] text-sm hover:text-[#0F172A] flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
    </div>
  </nav>
);

const TransferFormPage = () => {
  const [screen, setScreen] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState('');

  const [requester, setRequester] = useState({ name: '', email: '', phone: '', role: '' });
  const [athlete, setAthlete] = useState({ name: '', school: '', sport: '', position: '' });
  const [contractValue, setContractValue] = useState('');
  const [notes, setNotes] = useState('');

  const validate = () => {
    if (!requester.name || !requester.email || !requester.phone || !requester.role) return 'Please fill in all requester fields.';
    if (!athlete.name || !athlete.school || !athlete.sport) return 'Please fill in all athlete fields.';
    const val = Number(contractValue);
    if (!val || val < 10000) return 'Please enter a valid contract value (min $10,000).';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/quotes/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_type: 'transfer',
          requester,
          athlete: { ...athlete, coverageAmount: Number(contractValue) },
          notes
        })
      });
      if (!res.ok) throw new Error('Submission failed');
      const data = await res.json();
      setRequestId(data.requestId);

      await fetch(`${API}/quotes/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: data.requestId })
      });

      setScreen('confirmed');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setRequester({ name: '', email: '', phone: '', role: '' });
    setAthlete({ name: '', school: '', sport: '', position: '' });
    setContractValue(''); setNotes(''); setError(''); setRequestId('');
    setScreen('form');
    window.scrollTo(0, 0);
  };

  if (screen === 'confirmed') {
    const firstName = requester.name.split(' ')[0];
    return (
      <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Inter', sans-serif" }}>
        <Nav />
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-8">
            <div className="h-1 bg-gradient-to-r from-[#6B5CE7] via-[#4ECDC4] to-[#FFD93D]" />
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                <p className="text-[#065F46] text-sm font-semibold">Submission received</p>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
                We're on it.
              </h1>
              <p className="text-[#6B7280] text-base leading-relaxed">
                Thanks, <strong>{firstName}</strong>. A Players Health advisor will review your transfer insurance request and reach out to <strong>{requester.email}</strong> within 1–2 business days.
              </p>
            </div>
            <div className="mx-8 flex items-center gap-2 pb-6">
              <span className="text-sm text-[#6B7280]">Reference ID</span>
              <code className="bg-[#F3F4F6] px-3 py-1 rounded text-sm text-[#0F172A] font-mono">{requestId}</code>
            </div>
            <div className="bg-[#F9FAFB] px-8 py-6 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wider mb-3">Submission Summary</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#6B7280]">Athlete</span><span className="text-[#0F172A] font-medium">{athlete.name}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">School</span><span className="text-[#0F172A] font-medium">{athlete.school}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Sport</span><span className="text-[#0F172A] font-medium">{athlete.sport}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Contract Value</span><span className="text-[#0F172A] font-medium">{formatCurrency(Number(contractValue))}</span></div>
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <Link to="/insurance" className="text-sm text-[#6B7280] hover:underline">Back to home</Link>
            <button onClick={resetForm} className="text-sm text-[#6B5CE7] font-medium hover:underline">Submit another request</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Nav />
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="text-[#6B5CE7] text-[11px] font-semibold uppercase tracking-[0.12em] mb-2">Transfer Insurance</p>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
            Request Transfer Coverage
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">Protect your NIL investment when athletes transfer. A Players Health advisor will follow up with pricing.</p>
        </div>

        {/* Section 1: Your Info */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>01</span>
            <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Your info</h2>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">Full name<span className="text-red-500">*</span></label>
                <input type="text" value={requester.name} onChange={e => setRequester({...requester, name: e.target.value})}
                  className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                  data-testid="tf-req-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">Email<span className="text-red-500">*</span></label>
                <input type="email" value={requester.email} onChange={e => setRequester({...requester, email: e.target.value})}
                  className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                  data-testid="tf-req-email" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">Phone<span className="text-red-500">*</span></label>
                <input type="tel" value={requester.phone} onChange={e => setRequester({...requester, phone: e.target.value})}
                  className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                  data-testid="tf-req-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">Your role<span className="text-red-500">*</span></label>
                <div className="relative">
                  <select value={requester.role} onChange={e => setRequester({...requester, role: e.target.value})}
                    className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] appearance-none focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                    data-testid="tf-req-role">
                    <option value="">Pick one</option>
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Athlete Details */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>02</span>
            <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Athlete details</h2>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1.5">Athlete name<span className="text-red-500">*</span></label>
              <input type="text" value={athlete.name} onChange={e => setAthlete({...athlete, name: e.target.value})}
                className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                data-testid="tf-ath-name" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">School (D1)<span className="text-red-500">*</span></label>
                <div className="relative">
                  <select value={athlete.school} onChange={e => setAthlete({...athlete, school: e.target.value})}
                    className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] appearance-none focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                    data-testid="tf-ath-school">
                    <option value="">Pick a school</option>
                    {D1_SCHOOLS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#111827] mb-1.5">Sport<span className="text-red-500">*</span></label>
                <div className="relative">
                  <select value={athlete.sport} onChange={e => setAthlete({...athlete, sport: e.target.value})}
                    className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] appearance-none focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                    data-testid="tf-ath-sport">
                    <option value="">Pick a sport</option>
                    {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-1.5">Total NIL contract value (USD)<span className="text-red-500">*</span></label>
              <input type="number" value={contractValue} onChange={e => setContractValue(e.target.value)} min={10000}
                className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
                placeholder="Total contract value to insure" data-testid="tf-contract" />
              <p className="text-xs text-[#9CA3AF] mt-1">The total NIL contract value you want to protect against transfer.</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="mb-8">
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <label className="block text-sm font-medium text-[#111827] mb-1.5">Additional notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#6B5CE7] focus:ring-2 focus:ring-[#6B5CE7]/30 transition"
              placeholder="Anything else we should know (optional)" data-testid="tf-notes" />
          </div>
        </div>

        {error && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 mb-6 text-[#991B1B] text-sm" data-testid="tf-form-error">{error}</div>}

        <button type="submit" disabled={submitting}
          className="w-full h-12 bg-[#6B5CE7] text-white font-semibold rounded-md hover:bg-[#5B4ED6] transition disabled:opacity-50 text-base"
          data-testid="tf-submit"
        >{submitting ? 'Submitting...' : 'Submit for Quote'}</button>
      </form>
    </div>
  );
};

export default TransferFormPage;
